import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  Grid2,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useApi } from "../../api/client";
import AdminBar from "../../components/AdminBar";
import IssueBoard from "../../components/IssueBoard";
import { AllRoutes } from "../../config/routes";
import { ProjectDetail as ProjectDetailType, TimeEntry } from "../../types/projects";
import { formatDate, formatHours, formatMoney } from "../../utils/format";

interface EntryForm {
  date: Dayjs | null;
  hours: string;
  notes: string;
  paid: boolean;
}

const emptyEntry: EntryForm = {
  date: dayjs(),
  hours: "",
  notes: "",
  paid: false,
};

const TotalsCard: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Paper sx={{ p: 2, height: "100%" }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h4" sx={{ mb: 0 }}>
      {value}
    </Typography>
  </Paper>
);

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { request } = useApi();
  const [project, setProject] = useState<ProjectDetailType | null>(null);
  const [notes, setNotes] = useState("");
  const [entryForm, setEntryForm] = useState<EntryForm>(emptyEntry);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingNotes, setSavingNotes] = useState(false);
  const [savingEntry, setSavingEntry] = useState(false);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const loadProject = useCallback(async () => {
    if (!projectId) {
      return;
    }
    try {
      setError(null);
      const data = await request<ProjectDetailType>(`/api/projects/${projectId}`);
      setProject(data);
      setNotes(data.notes);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load project");
    }
  }, [projectId, request]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  const saveNotes = async () => {
    if (!project) {
      return;
    }
    setSavingNotes(true);
    try {
      await request(`/api/projects/${project.id}`, {
        method: "PATCH",
        body: JSON.stringify({ notes }),
      });
      setSnackbar("Project notes saved");
      await loadProject();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  const resetEntryForm = () => {
    setEditingEntry(null);
    setEntryForm(emptyEntry);
  };

  const saveEntry = async () => {
    if (!project || !entryForm.date) {
      setError("Date is required");
      return;
    }
    const hours = Number(entryForm.hours);
    if (!Number.isFinite(hours) || hours <= 0 || hours > 24) {
      setError("Hours must be between 0 and 24");
      return;
    }

    setSavingEntry(true);
    try {
      const payload = {
        date: entryForm.date.toISOString(),
        hours,
        notes: entryForm.notes,
        paid: entryForm.paid,
      };
      if (editingEntry) {
        await request(`/api/entries/${editingEntry.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
      } else {
        await request(`/api/projects/${project.id}/entries`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      resetEntryForm();
      await loadProject();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save time entry");
    } finally {
      setSavingEntry(false);
    }
  };

  const startEdit = (entry: TimeEntry) => {
    setEditingEntry(entry);
    setEntryForm({
      date: dayjs(entry.date),
      hours: String(entry.hours),
      notes: entry.notes,
      paid: entry.paid,
    });
  };

  const togglePaid = async (entry: TimeEntry) => {
    try {
      await request(`/api/entries/${entry.id}`, {
        method: "PATCH",
        body: JSON.stringify({ paid: !entry.paid }),
      });
      await loadProject();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update entry");
    }
  };

  const deleteEntry = async (entry: TimeEntry) => {
    if (!window.confirm("Delete this time entry?")) {
      return;
    }
    try {
      await request(`/api/entries/${entry.id}`, { method: "DELETE" });
      if (editingEntry?.id === entry.id) {
        resetEntryForm();
      }
      await loadProject();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete entry");
    }
  };

  const copyPublicLink = async () => {
    if (!project) {
      return;
    }
    const url = `${window.location.origin}${AllRoutes.Hours}/${project.id}`;
    await navigator.clipboard.writeText(url);
    setSnackbar("Public hours link copied");
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      <AdminBar title={project?.name || "Project"} />
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <Button
            component={RouterLink}
            to={AllRoutes.Projects}
            startIcon={<ArrowBackIcon />}
            sx={{ textTransform: "none" }}
          >
            All projects
          </Button>
          {project && (
            <Button
              startIcon={<ContentCopyIcon />}
              onClick={copyPublicLink}
              sx={{ textTransform: "none" }}
            >
              Copy public link
            </Button>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!project ? (
          <Typography>Loading project…</Typography>
        ) : (
          <>
            <Typography variant="h3" sx={{ mb: 1 }}>
              {project.name}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Started {formatDate(project.startDate)} ·{" "}
              {formatMoney(project.hourlyRate)} / hour
            </Typography>

            <Grid2 container spacing={2} sx={{ mb: 4 }}>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <TotalsCard
                  label="Total hours"
                  value={formatHours(project.totals.totalHours)}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <TotalsCard
                  label="Unpaid hours"
                  value={formatHours(project.totals.pendingHours)}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <TotalsCard
                  label="Total"
                  value={formatMoney(project.totals.totalAmount)}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <TotalsCard
                  label="Unpaid"
                  value={formatMoney(project.totals.pendingAmount)}
                />
              </Grid2>
            </Grid2>

            <IssueBoard
              projectId={project.id}
              issues={project.issues ?? []}
              onCopyToLog={(text) => {
                setEntryForm((current) => ({
                  ...current,
                  notes: current.notes.trim()
                    ? `${current.notes.trim()}\n${text}`
                    : text,
                }));
                setSnackbar("Copied to worked day notes");
              }}
            />

            <Paper sx={{ p: 3, mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>
                Project notes
              </Typography>
              <TextField
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                multiline
                minRows={3}
                fullWidth
                placeholder="Private notes for this project"
              />
              <Button
                variant="contained"
                sx={{ mt: 2 }}
                onClick={saveNotes}
                disabled={savingNotes}
              >
                {savingNotes ? "Saving…" : "Save notes"}
              </Button>
            </Paper>

            <Paper sx={{ p: 3, mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2 }}>
                {editingEntry ? "Edit worked day" : "Log a worked day"}
              </Typography>
              <Grid2 container spacing={2} alignItems="center">
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                  <DatePicker
                    label="Date"
                    value={entryForm.date}
                    onChange={(value) =>
                      setEntryForm((current) => ({ ...current, date: value }))
                    }
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                  <TextField
                    label="Hours"
                    type="number"
                    value={entryForm.hours}
                    onChange={(event) =>
                      setEntryForm((current) => ({
                        ...current,
                        hours: event.target.value,
                      }))
                    }
                    inputProps={{ min: 0.25, max: 24, step: "0.25" }}
                    fullWidth
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 4 }}>
                  <TextField
                    label="Notes"
                    value={entryForm.notes}
                    onChange={(event) =>
                      setEntryForm((current) => ({
                        ...current,
                        notes: event.target.value,
                      }))
                    }
                    fullWidth
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 3 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={entryForm.paid}
                        onChange={(event) =>
                          setEntryForm((current) => ({
                            ...current,
                            paid: event.target.checked,
                          }))
                        }
                      />
                    }
                    label="Paid"
                  />
                </Grid2>
              </Grid2>
              <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={saveEntry}
                  disabled={savingEntry}
                >
                  {savingEntry
                    ? "Saving…"
                    : editingEntry
                      ? "Update entry"
                      : "Add entry"}
                </Button>
                {editingEntry && (
                  <Button onClick={resetEntryForm}>Cancel</Button>
                )}
              </Box>
            </Paper>

            <Paper>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Hours</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>Paid</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {project.entries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>No time entries yet.</TableCell>
                    </TableRow>
                  ) : (
                    project.entries.map((entry) => (
                      <TableRow key={entry.id} hover>
                        <TableCell>{formatDate(entry.date)}</TableCell>
                        <TableCell align="right">
                          {formatHours(entry.hours)}
                        </TableCell>
                        <TableCell align="right">
                          {formatMoney(entry.hours * project.hourlyRate)}
                        </TableCell>
                        <TableCell>{entry.notes || "—"}</TableCell>
                        <TableCell>
                          <Checkbox
                            checked={entry.paid}
                            onChange={() => togglePaid(entry)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            aria-label="Edit entry"
                            onClick={() => startEdit(entry)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            aria-label="Delete entry"
                            onClick={() => deleteEntry(entry)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Paper>
          </>
        )}
      </Container>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={2500}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </Box>
  );
};

export default ProjectDetail;
